import { useEffect, useMemo, useState } from "react";

type Variant = "inline" | "block" | "overlay" | "fullscreen";

type LoaderProps = {
  text?: string;
  variant?: Variant;
  delayMs?: number
  minHeight?: number;
  small?: boolean;
  children?: React.ReactNode;
};

let _stylesInjected = false;
function ensureStyles() {
  if (_stylesInjected) return;
  const css = `
  @keyframes loader-spin { 
    100% { transform: rotate(360deg); } 
  }
  .ld-root { display:flex; align-items:center; gap:10px; color:#374151; }
  .ld-text { font-size:14px; line-height:1.2; }
  .ld-text.small { font-size:12px; }
  .ld-spinner {
    width:28px; height:28px; border-radius:50%;
    border:3px solid rgba(0,0,0,0.1);
    border-top-color:#111827; animation:loader-spin 0.9s linear infinite;
  }
  .ld-spinner.small { width:20px; height:20px; border-width:2px; }
  .ld-block {
    display:flex; align-items:center; justify-content:center;
    width:100%; min-height:160px;
  }
  .ld-overlay, .ld-fullscreen {
    position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
    background:rgba(255,255,255,0.7); backdrop-filter:saturate(160%) blur(2px);
    z-index: 50;
  }
  .ld-fullscreen { position:fixed; }
  .ld-stack { display:flex; flex-direction:column; align-items:center; gap:10px; }
  `;
  const tag = document.createElement("style");
  tag.setAttribute("data-loader-styles", "true");
  tag.textContent = css;
  document.head.appendChild(tag);
  _stylesInjected = true;
}

export function Loader({
  text = "Loading",
  variant = "inline",
  delayMs = 250,
  minHeight = 160,
  small = false,
  children,
}: LoaderProps) {
  ensureStyles();

  const [show, setShow] = useState(delayMs === 0);
  useEffect(() => {
    if (delayMs === 0) return;
    const t = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  const spinner = useMemo(
    () => <div className={`ld-spinner ${small ? "small" : ""}`} aria-hidden="true" />,
    [small]
  );

  if (!show) return null;

  if (variant === "inline") {
    return (
      <div className="ld-root" role="status" aria-busy="true">
        {spinner}
        <span className={`ld-text ${small ? "small" : ""}`}>{text}</span>
      </div>
    );
  }

  if (variant === "block") {
    return (
      <div className="ld-block" role="status" aria-busy="true" style={{ minHeight }}>
        <div className="ld-root ld-stack">
          {spinner}
          <span className={`ld-text ${small ? "small" : ""}`}>{text}</span>
        </div>
      </div>
    );
  }

  if (variant === "overlay" || variant === "fullscreen") {
    return (
      <div className={variant === "overlay" ? "ld-overlay" : "ld-fullscreen"} role="status" aria-busy="true">
        <div className="ld-root ld-stack">
          {spinner}
          <span className={`ld-text ${small ? "small" : ""}`}>{text}</span>
          {children}
        </div>
      </div>
    );
  }

  return null;
}

export function TableLoaderRow({
  colSpan = 6,
  text = "Loading…",
  minHeight = 120,
}: { colSpan?: number; text?: string; minHeight?: number }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <Loader variant="block" text={text} minHeight={minHeight} />
      </td>
    </tr>
  );
}
